import { useMutation, useQuery } from '@tanstack/react-query';
import { announceSavedOffline } from '@/stores/savedOffline';
import { fetchOrCached, isOfflineGap, NOT_ON_THIS_PC } from '@/offline/availability';
import { LoadingNote, OfflineDetails } from '@/components/OfflineDetails';
import { useSavedWork } from '@/offline/useSavedWork';
import { useEffect, useState } from 'react';
import { api, UNREACHABLE_MESSAGE } from '@/api/client';
import { awaitWork, retryWork, saveWork, savedWork, takeBack } from '@/offline/engine';
import type { NewOperation, Operation } from '@/offline/outbox';
import { confirmAction } from '@/stores/confirm';
import { ActionBar, DataGrid, type DesktopAction, type GridColumn } from '@/components/desktop';
import { DocumentFrame } from '@/components/DocumentFrame';
import { SavedWorkDetail } from '@/components/SavedWorkDetail';
import { piecesOf } from '@/offline/pieces';
import { AppIcon } from '@/components/AppIcon';
import { StorePicker } from '@/components/StorePicker';
import { ComposePanel } from '@/components/ComposePanel';
import { DeleteButton, HistoryPanel, ReverseButton, ReviewSummaryDialog, printDocument } from '@/components/documents';
import { DraftNotice } from '@/components/DraftNotice';
import { DrawLines, emptyDrawLine, type DrawLine } from '@/components/DrawLines';
import { useDraft } from '@/stores/drafts';
import {
  ErrorNote,
  Field,
  FormActions,
  FormGroup,
  OkNote,
  Pager,
  Select,
  StatusPill,
  Table,
  TextArea,
  Tile,
  errorMessage,
  lineQuantity,
  money,
  rate,
  quantity,
  shortDate,
  todayIso,
} from '@/components/ui';
import { useSkuCatalog, useStockInvalidation, useStores } from '@/hooks/inventory';
import { useFarmScopedStore } from '@/hooks/useFarmScopedStore';
import { useAuth } from '@/stores/auth';
import { farmScopeQuery, useFarmScope } from '@/stores/farmScope';
import type { ScreenIntent } from '@/stores/shellNav';
import { useScreenIntent } from '@/stores/shellNav';
import { packedSuffix } from '@/packing';

const PAGE_SIZE = 50;

function storeLabel(s: { code: string; nameEn: string }): string {
  return `${s.code} — ${s.nameEn}`;
}

interface AdjustmentDetail {
  id: string;
  documentNo: string;
  status: string;
  approval: string;
  storeId: string;
  store: string;
  storeCode: string;
  adjustmentDate: string;
  reason: string;
  totalQty: string;
  totalValue?: string;
  version: number;
  raisedByYou: boolean;
  postedAt: string | null;
  reversedAt: string | null;
  reversalReason: string | null;
  reviewComment: string | null;
  reviewedAt: string | null;
  lines: Array<{
    id: string;
    skuId: string;
    skuCode: string;
    skuName: string;
    qty: string;
    unitCost?: string;
    value?: string;
    batchId: string | null;
    batchNo: string | null;
    expiryDate: string | null;
  }>;
}

interface AdjustmentRow {
  id: string;
  documentNo: string;
  status: string;
  approval: string;
  adjustmentDate: string;
  store: string;
  storeCode: string;
  reason: string;
  totalQty: string;
  totalValue?: string;
  raisedByYou: boolean;
}

const ADJUSTMENTS = '/api/v1/inventory/adjustments';
const LATER = 'Saved on this PC. It will be done when the connection is back.';

/**
 * What a queued adjustment carries besides its request: the form as it was
 * typed. It is what the list shows for work not yet sent, and what Fix it
 * loads back (.docs/023).
 */
export interface QueuedAdjustmentForm {
  header: { storeId: string; adjustmentDate: string; reason: string; direction: string };
  lines: DrawLine[];
}

/**
 * Saves one action on a document through the outbox (.docs/023): answered
 * now with a line, kept on this PC without one. A refusal while the screen is
 * open is taken back out and thrown, for the screen to show.
 */
async function saveOne(op: NewOperation): Promise<'sent' | 'waiting'> {
  const saved = await saveWork([op]);
  // Kept for later: said so where it cannot be missed.
  if (saved.outcome === 'waiting') void announceSavedOffline({ what: op.describes, closeLabel: 'OK' });
  if (saved.outcome === 'sent' || saved.outcome === 'waiting') return saved.outcome;
  for (const o of saved.ops) if (o.state !== 'sent') await takeBack(o.id).catch(() => undefined);
  throw new Error(saved.ops.find((o) => o.state === 'refused')?.lastError ?? UNREACHABLE_MESSAGE);
}

/** An adjustment saved on this PC, shown in the list until the server has it. */
function queuedRow(op: Operation, stores: ReadonlyArray<{ id: string; code: string }>): AdjustmentRow {
  const form = op.form as QueuedAdjustmentForm;
  const sign = form.header.direction === '-1' ? -1 : 1;
  return {
    id: `op:${op.id}`,
    documentNo: 'Not sent yet',
    status: op.state === 'refused' ? 'REFUSED' : 'NOT_SENT',
    approval: 'PENDING',
    adjustmentDate: form.header.adjustmentDate,
    reason: form.header.reason,
    storeCode: stores.find((s) => s.id === form.header.storeId)?.code ?? '',
    totalQty: String(sign * form.lines.reduce((sum, l) => sum + (Number(l.qty) || 0), 0)),
  } as unknown as AdjustmentRow;
}

type ComposeState =
  | { mode: 'create'; intent?: ScreenIntent | undefined; seed?: AdjustmentDetail | undefined }
  | { mode: 'edit'; detail: AdjustmentDetail }
  /** Work the server refused, loaded back to be put right (.docs/023). */
  | { mode: 'fix'; op: Operation };

/** An adjustment's own screen: what changed in which store and why, where its approval stands, and its history. */
function AdjustmentDocument({ detail, showCost }: { detail: AdjustmentDetail; showCost: boolean }) {
  const pending = detail.status === 'DRAFT' && detail.approval === 'PENDING';
  return (
    <>
      {detail.reversedAt && (
        <p className="docpage__note docpage__note--alert" role="note">
          Reversed on {shortDate(detail.reversedAt)}. Reason: {detail.reversalReason ?? '—'}
        </p>
      )}
      {detail.approval === 'REJECTED' && (
        <p className="docpage__note docpage__note--alert" role="note">
          Rejected{detail.reviewedAt ? ` on ${shortDate(detail.reviewedAt)}` : ''}.
          {detail.reviewComment ? ` Comment: ${detail.reviewComment}` : ''} Nothing changed in stock.
        </p>
      )}
      {pending && (
        <p className="docpage__note" role="note">
          {detail.raisedByYou
            ? 'Waiting for someone else to approve it. Nothing changes in stock until it is approved and posted.'
            : 'Waiting for approval. Nothing changes in stock until it is approved and posted.'}
        </p>
      )}
      {detail.status === 'DRAFT' && detail.approval === 'APPROVED' && (
        <p className="docpage__note" role="note">
          Approved. Stock changes once it is posted.
        </p>
      )}

      <div className="tiles">
        <Tile
          label="Net quantity"
          value={quantity(detail.totalQty, 2)}
          note={Number(detail.totalQty) < 0 ? 'Written off' : 'Written on'}
        />
        {showCost && <Tile label="Value" value={money(detail.totalValue)} />}
        <Tile label="Store" value={detail.storeCode} />
        <Tile label="Items" value={String(detail.lines.length)} />
      </div>

      <section className="stack docpage__section">
        <h3 className="formsection">Adjustment</h3>
        <dl className="detaillist">
          <dt>Store</dt>
          <dd>
            {detail.storeCode} — {detail.store}
          </dd>
          <dt>Date</dt>
          <dd>{shortDate(detail.adjustmentDate)}</dd>
          <dt>Reason</dt>
          <dd>{detail.reason}</dd>
          <dt>Approval</dt>
          <dd>
            <StatusPill status={detail.approval} />
            {detail.raisedByYou && <span className="muted"> · raised by you</span>}
          </dd>
          {detail.reviewedAt && (
            <>
              <dt>Reviewed on</dt>
              <dd>{shortDate(detail.reviewedAt)}</dd>
            </>
          )}
          {detail.reviewComment && detail.approval !== 'REJECTED' && (
            <>
              <dt>Review comment</dt>
              <dd>{detail.reviewComment}</dd>
            </>
          )}
          {detail.postedAt && (
            <>
              <dt>Posted on</dt>
              <dd>{shortDate(detail.postedAt)}</dd>
            </>
          )}
        </dl>
      </section>

      <section className="stack docpage__section">
        <h3 className="formsection">Items</h3>
        <Table
          count={detail.lines.length}
          empty="No items."
          head={
            <tr>
              <th>Item</th>
              <th className="num">Quantity</th>
              {showCost && <th className="num">Unit cost</th>}
              {showCost && <th className="num">Value</th>}
              <th>Lot</th>
            </tr>
          }
        >
          {detail.lines.map((l) => (
            <tr key={l.id}>
              <td>
                <span className="mono">{l.skuCode}</span> {l.skuName}
              </td>
              <td className="num">{lineQuantity(l.qty)}</td>
              {showCost && <td className="num">{rate(l.unitCost)}</td>}
              {showCost && <td className="num">{money(l.value)}</td>}
              <td className="dim">
                {l.batchNo ? `${l.batchNo}${l.expiryDate ? ` · expires ${shortDate(l.expiryDate)}` : ''}` : '—'}
              </td>
            </tr>
          ))}
        </Table>
      </section>

      <section className="stack docpage__section">
        <h3 className="formsection">History</h3>
        <HistoryPanel path={`/api/v1/inventory/adjustments/${detail.id}/history`} />
      </section>
    </>
  );
}

export function Adjustments() {
  const intent = useScreenIntent('inv-adjust');
  const { can } = useAuth();
  const invalidate = useStockInvalidation();
  const farmIds = useFarmScope((s) => s.selectedFarmIds);
  const showCost = can('inv.price.read');
  const [compose, setCompose] = useState<ComposeState | null>(() =>
    intent?.action === 'create' && can('inv.adjustment.create') ? { mode: 'create', intent } : null,
  );
  const [skip, setSkip] = useState(0);
  const [selectedKey, setSelectedKey] = useState<string | null>(intent?.recordId ?? null);
  // Sent here to open one adjustment: from a stock card's line, or from Offline work once it has gone.
  const [openKey, setOpenKey] = useState<string | null>(() => intent?.recordId ?? null);
  const [note, setNote] = useState<string | null>(null);
  const [repeatBusy, setRepeatBusy] = useState(false);
  const { data: stores } = useStores();

  // Work saved on this PC and not yet with the server leads the list
  // (.docs/023): what somebody is still waiting on, with no number yet.
  const outbox = useSavedWork();
  const queued = (outbox.data ?? [])
    .filter((op) => op.kind === 'adjustment.create' && op.state !== 'sent' && op.form !== undefined)
    .reverse();
  const opFor = (row: AdjustmentRow) => queued.find((op) => `op:${op.id}` === row.id);

  // Sent here from Offline work to put refused work right.
  useEffect(() => {
    const opId = intent?.fixOperation;
    if (!opId) return;
    void savedWork().then((ops) => {
      const op = ops.find((o) => o.id === opId);
      if (op?.form) setCompose({ mode: 'fix', op });
    });
  }, [intent?.fixOperation]);

  async function throwAway(op: Operation) {
    const ok = await confirmAction({
      title: 'Throw this work away?',
      message: `"${op.describes}" will be removed from this PC. The server will never see it.`,
      confirmLabel: 'Throw it away',
      cancelLabel: 'Keep it',
      danger: true,
    });
    if (!ok) return;
    await takeBack(op.id);
    setSelectedKey(null);
    setNote('Thrown away.');
  }

  const list = useQuery({
    queryKey: ['inv', 'adjustments', farmIds, skip],
    queryFn: () => api.get('/api/v1/inventory/adjustments', { take: PAGE_SIZE, skip, ...farmScopeQuery(farmIds) }),
  });
  const { data, isPending, error } = list;

  const detailQuery = useQuery({
    queryKey: ['inv', 'adjustments', 'detail', openKey],
    queryFn: async () =>
      (await api.get(`/api/v1/inventory/adjustments/${openKey}` as '/api/v1/inventory/adjustments/{id}')) as unknown as AdjustmentDetail,
    // A row saved on this PC has no adjustment on the server yet.
    enabled: openKey !== null && !openKey.startsWith('op:'),
  });
  const { data: detail, error: detailError, isFetching: detailFetching, refetch: refetchDetail } = detailQuery;

  // Every action goes through the outbox (.docs/023). A decision carries the
  // version the approver saw, so an adjustment the maker changed since is
  // refused rather than approved unseen; the server still makes sure the
  // approver is not who raised it, whenever the decision arrives.
  type Doc = { id: string; documentNo: string; version?: number | undefined };
  const decide = (verb: 'approve' | 'reject') => (doc: Doc) =>
    saveOne({
      kind: verb === 'approve' ? 'adjustment.approve' : 'adjustment.reject',
      method: 'POST',
      path: `${ADJUSTMENTS}/${doc.id}/${verb}`,
      ...(doc.version ? { body: { version: doc.version } } : {}),
      describes: `${verb === 'approve' ? 'Approving' : 'Rejecting'} ${doc.documentNo}`,
      screen: 'inv-adjust',
    });
  const approve = useMutation({
    mutationFn: decide('approve'),
    onSuccess: (outcome) => { invalidate(); void refetchDetail(); setNote(outcome === 'sent' ? 'Approved. It can now be posted.' : LATER); },
  });
  const reject = useMutation({
    mutationFn: decide('reject'),
    onSuccess: (outcome) => { invalidate(); void refetchDetail(); setNote(outcome === 'sent' ? 'Rejected.' : LATER); },
  });
  const post = useMutation({
    mutationFn: (doc: Doc) =>
      saveOne({
        kind: 'adjustment.post',
        method: 'POST',
        path: `${ADJUSTMENTS}/${doc.id}/post`,
        ...(doc.version ? { body: { version: doc.version } } : {}),
        describes: `Posting ${doc.documentNo}`,
        screen: 'inv-adjust',
      }),
    onSuccess: (outcome) => { invalidate(); void refetchDetail(); setNote(outcome === 'sent' ? 'Posted to the ledger.' : LATER); },
  });
  const del = useMutation({
    mutationFn: ({ id, version, documentNo }: { id: string; version: number; documentNo: string }) =>
      saveOne({
        kind: 'adjustment.cancel',
        method: 'DELETE',
        path: `${ADJUSTMENTS}/${id}?version=${version}`,
        describes: `Deleting draft ${documentNo}`,
        screen: 'inv-adjust',
      }),
    // A deleted draft has no page left to stand on: back to the list, saying so.
    onSuccess: (outcome) => { invalidate(); setSelectedKey(null); setOpenKey(null); setNote(outcome === 'sent' ? 'Draft deleted.' : LATER); },
  });
  const reverse = useMutation({
    mutationFn: ({ id, reason, documentNo }: { id: string; reason: string; documentNo: string }) =>
      saveOne({
        kind: 'adjustment.reverse',
        method: 'POST',
        path: `${ADJUSTMENTS}/${id}/reverse`,
        body: { reason },
        describes: `Reversing ${documentNo}`,
        screen: 'inv-adjust',
      }),
    onSuccess: (outcome) => { invalidate(); void refetchDetail(); setNote(outcome === 'sent' ? 'Reversed.' : LATER); },
  });
  const acting = [approve, reject, post, del, reverse];
  const failed = approve.error ?? reject.error ?? post.error ?? del.error ?? reverse.error;

  const rows = skip === 0 ? [...queued.map((op) => queuedRow(op, stores ?? [])), ...(data?.items ?? [])] : (data?.items ?? []);

  /** Moving between the list and an adjustment: what was said on one is not news on the other. */
  function show(key: string | null) {
    setOpenKey(key);
    if (key) setSelectedKey(key);
    setNote(null);
    for (const m of acting) m.reset();
  }

  /** The whole document: from the server, or as this PC last loaded it. */
  async function fetchFull(id: string): Promise<AdjustmentDetail> {
    return fetchOrCached(
      ['inv', 'adjustments', 'detail', id],
      async () => (await api.get(`/api/v1/inventory/adjustments/${id}` as '/api/v1/inventory/adjustments/{id}')) as unknown as AdjustmentDetail,
      'This document',
    );
  }

  async function edit(row: AdjustmentRow) {
    try {
      setCompose({ mode: 'edit', detail: await fetchFull(row.id) });
    } catch (e) {
      setNote(errorMessage(e, 'Could not load that adjustment to edit it'));
    }
  }

  async function repeat(row: AdjustmentRow) {
    setRepeatBusy(true);
    try {
      setCompose({ mode: 'create', seed: await fetchFull(row.id) });
    } catch (e) {
      setNote(errorMessage(e, 'Could not load that adjustment to repeat it'));
    } finally {
      setRepeatBusy(false);
    }
  }

  async function print(row: AdjustmentRow) {
    try {
      printAdjustment(await fetchFull(row.id));
    } catch (e) {
      setNote(errorMessage(e, 'Could not load that adjustment to print it'));
    }
  }

  function printAdjustment(full: AdjustmentDetail) {
    printDocument({
      title: `Adjustment ${full.documentNo}`,
      facts: [
        ['Status', full.status],
        ['Approval', full.approval],
        ['Store', `${full.storeCode} — ${full.store}`],
        ['Date', shortDate(full.adjustmentDate)],
        ['Reason', full.reason],
        ['Net quantity', quantity(full.totalQty, 2)],
        ...(showCost ? ([['Total value', money(full.totalValue)]] as Array<[string, string]>) : []),
      ],
      lines: full.lines.map((l) => ({
        item: `${l.skuCode} — ${l.skuName}`,
        qty: quantity(l.qty, 4),
        ...(l.unitCost ? { unitCost: rate(l.unitCost) } : {}),
        ...(l.value ? { value: money(l.value) } : {}),
        ...(l.batchNo ? { lot: l.batchNo } : {}),
      })),
      showCost,
    });
  }

  function rowActions(row: AdjustmentRow): DesktopAction[] {
    const op = opFor(row);
    if (op) {
      return [
        { label: 'Open', icon: 'details' as const, shortcut: 'Enter', onSelect: () => show(row.id), allowed: true },
        {
          label: 'Fix it', icon: 'edit' as const,
          onSelect: () => setCompose({ mode: 'fix', op }),
          allowed: can('inv.adjustment.create'),
          enabled: op.state === 'refused',
          reason: 'Only work the server refused needs fixing',
        },
        {
          label: 'Throw it away', icon: 'remove' as const,
          onSelect: () => void throwAway(op),
          allowed: true,
          enabled: op.state === 'refused',
          reason: 'Work still waiting to be sent cannot be thrown away',
        },
      ];
    }
    const seen = { id: row.id, documentNo: row.documentNo, version: detail?.id === row.id ? detail.version : undefined };
    return [
      { label: 'Open', icon: 'details' as const, shortcut: 'Enter', onSelect: () => show(row.id), allowed: true },
      {
        label: 'Edit', icon: 'edit' as const,
        onSelect: () => void edit(row),
        allowed: can('inv.adjustment.update'),
        enabled: row.status === 'DRAFT' && row.approval === 'PENDING',
        reason: 'Only a pending draft can be edited',
      },
      {
        label: 'Approve', icon: 'approve' as const,
        onSelect: () => approve.mutate(seen),
        allowed: can('inv.adjustment.approve'),
        enabled: row.approval === 'PENDING' && !row.raisedByYou,
        reason: row.raisedByYou ? 'Whoever raised it cannot approve it' : 'Only a pending adjustment can be approved',
        separatorBefore: true,
      },
      {
        label: 'Reject', icon: 'reject' as const,
        onSelect: () => reject.mutate(seen),
        allowed: can('inv.adjustment.reject'),
        enabled: row.approval === 'PENDING' && !row.raisedByYou,
        reason: row.raisedByYou ? 'Whoever raised it cannot reject it' : 'Only a pending adjustment can be rejected',
      },
      {
        label: 'Post', icon: 'post' as const,
        onSelect: () => post.mutate(seen),
        allowed: can('inv.adjustment.post'),
        enabled: row.approval === 'APPROVED' && row.status === 'DRAFT',
        reason: 'Only an approved draft can be posted',
      },
      { label: 'Repeat', icon: 'repeat' as const, onSelect: () => void repeat(row), allowed: can('inv.adjustment.create'), enabled: !repeatBusy, separatorBefore: true },
      { label: 'Print', icon: 'print' as const, onSelect: () => void print(row), allowed: true },
    ];
  }

  const toolbarActions: DesktopAction[] = [
    { label: 'Raise adjustment', icon: 'adjust', primary: true, onSelect: () => setCompose({ mode: 'create' }), allowed: can('inv.adjustment.create') },
  ];

  const columns: Array<GridColumn<AdjustmentRow>> = [
    { key: 'doc', header: 'Document', width: 150, render: (r) => <span className="withicon"><AppIcon kind="adjust" size={14} /><span className="mono">{r.documentNo}</span></span> },
    { key: 'date', header: 'Date', width: 100, render: (r) => shortDate(r.adjustmentDate) },
    { key: 'store', header: 'Store', width: 100, render: (r) => <span className="dim">{r.storeCode}</span> },
    { key: 'reason', header: 'Reason', width: 220, render: (r) => r.reason },
    { key: 'qty', header: 'Net quantity', align: 'end', width: 110, render: (r) => quantity(r.totalQty, 2) },
    { key: 'approval', header: 'Approval', width: 110, render: (r) => <StatusPill status={r.approval} /> },
    { key: 'status', header: 'Status', width: 110, render: (r) => <StatusPill status={r.status} /> },
  ];

  if (compose) {
    return (
      <AdjustmentCompose
        state={compose}
        onCancel={() => setCompose(null)}
        onSaved={(message) => { setCompose(null); setNote(message); }}
      />
    );
  }

  // Work saved on this PC: shown as it was typed until the server has it.
  const savedPiece = openKey?.startsWith('op:')
    ? piecesOf(outbox.data ?? []).find((p) => p.ops.some((op) => `op:${op.id}` === openKey))
    : undefined;
  if (savedPiece) {
    return (
      <SavedWorkDetail
        piece={savedPiece}
        all={outbox.data ?? []}
        backLabel="Back to Stock Adjustments"
        onBack={() => show(null)}
        onFix={(op) => { show(null); setCompose({ mode: 'fix', op }); }}
        onOpenDocument={(id) => show(id)}
        onDone={(said) => { show(null); setNote(said); }}
      />
    );
  }

  if (openKey && !openKey.startsWith('op:')) {
    const openRow = rows.find((r) => r.id === openKey);
    const pending = detail?.status === 'DRAFT' && detail.approval === 'PENDING';
    const deciding = pending && !detail.raisedByYou;
    const canEdit = pending && can('inv.adjustment.update');
    const canDelete = pending && can('inv.adjustment.delete');
    const canApprove = deciding && can('inv.adjustment.approve');
    const canReject = deciding && can('inv.adjustment.reject');
    const canPost = detail?.status === 'DRAFT' && detail.approval === 'APPROVED' && can('inv.adjustment.post');
    const canReverse = detail?.status === 'POSTED' && can('inv.adjustment.reverse');
    const busy = acting.some((m) => m.isPending) || detailFetching;
    return (
      <DocumentFrame
        onBack={() => show(null)}
        backLabel="Back to Stock Adjustments"
        title={
          detail && (
            <>
              <h2 className="withicon">
                <AppIcon kind="adjust" size={18} />
                <span className="mono">{detail.documentNo}</span>
              </h2>
              <StatusPill status={detail.status} />
              <span className="muted">
                {detail.storeCode} · {shortDate(detail.adjustmentDate)}
              </span>
            </>
          )
        }
        actions={
          detail && (
            <>
              <button type="button" className="btn btn--withicon" onClick={() => printAdjustment(detail)}>
                <AppIcon kind="print" size={15} />
                Print
              </button>
              {can('inv.adjustment.create') && (
                <button
                  type="button"
                  className="btn btn--withicon"
                  onClick={() => {
                    // Leaves this adjustment behind: "Back to list" means the list.
                    show(null);
                    setCompose({ mode: 'create', seed: detail });
                  }}
                >
                  <AppIcon kind="repeat" size={15} />
                  Repeat
                </button>
              )}
              {canDelete && (
                <DeleteButton pending={del.isPending} onDelete={() => del.mutate({ id: detail.id, version: detail.version, documentNo: detail.documentNo })} />
              )}
              {canReverse && (
                <ReverseButton pending={reverse.isPending} onReverse={(reason) => reverse.mutate({ id: detail.id, reason, documentNo: detail.documentNo })} />
              )}
              {canEdit && (
                <button type="button" className="btn btn--withicon" onClick={() => setCompose({ mode: 'edit', detail })}>
                  <AppIcon kind="edit" size={15} />
                  Edit
                </button>
              )}
              {canReject && (
                <button type="button" className="btn btn--withicon" disabled={busy} onClick={() => reject.mutate(detail)}>
                  <AppIcon kind="reject" size={15} />
                  {reject.isPending ? 'Rejecting…' : 'Reject'}
                </button>
              )}
              {canApprove && (
                <button type="button" className="btn btn--primary btn--withicon" disabled={busy} onClick={() => approve.mutate(detail)}>
                  <AppIcon kind="approve" size={15} />
                  {approve.isPending ? 'Approving…' : 'Approve'}
                </button>
              )}
              {canPost && (
                <button type="button" className="btn btn--primary btn--withicon" disabled={busy} onClick={() => post.mutate(detail)}>
                  <AppIcon kind="post" size={15} />
                  {post.isPending ? 'Posting…' : 'Post'}
                </button>
              )}
            </>
          )
        }
      >
        {note && <OkNote message={note} />}
        {failed && <ErrorNote message={errorMessage(failed, 'Action failed')} />}
        {detailError && !detail && <ErrorNote message={detailError.message} />}
        {!detail && isOfflineGap(detailQuery) && openRow && <OfflineDetails row={openRow} columns={columns} />}
        {!detail && !detailError && !(isOfflineGap(detailQuery) && openRow) && <LoadingNote what="Loading the adjustment…" />}
        {detail && <AdjustmentDocument detail={detail} showCost={showCost} />}
      </DocumentFrame>
    );
  }

  return (
    <div className="workbench">
      <div className="workbench__main">
        <ActionBar actions={toolbarActions} />

        {note && <OkNote message={note} />}
        {failed && <ErrorNote message={errorMessage(failed, 'Action failed')} />}
        {error && <ErrorNote message={error.message} />}

        <DataGrid
          id="documents-adjustments"
          label="Adjustments"
          columns={columns}
          rows={rows}
          rowKey={(r) => r.id}
          selectedKey={selectedKey}
          onSelect={setSelectedKey}
          // A row saved on this PC opens as what was saved, and where it has got to.
          onOpen={(row) => show(row.id)}
          openOnClick
          actions={rowActions}
          loading={isPending && !isOfflineGap(list)}
          empty={isOfflineGap(list) ? NOT_ON_THIS_PC : 'No adjustments raised.'}
        />
        <Pager skip={skip} take={PAGE_SIZE} total={data?.total ?? 0} onChange={setSkip} />
      </div>
    </div>
  );
}

function AdjustmentCompose({
  state,
  onCancel,
  onSaved,
}: {
  state: ComposeState;
  onCancel: () => void;
  onSaved: (message: string) => void;
}) {
  const invalidate = useStockInvalidation();
  const { data: stores } = useStores();
  const catalog = useSkuCatalog();

  const initial = state.mode === 'edit' ? state.detail : undefined;
  const seed = initial ?? (state.mode === 'create' ? state.seed : undefined);
  const intent = state.mode === 'create' ? state.intent : undefined;
  const editing = initial !== undefined;
  // Refused work being put right: its form comes back as it was typed.
  const fixing = state.mode === 'fix' ? state.op : undefined;
  const fixForm = fixing?.form as QueuedAdjustmentForm | undefined;
  const seedFirstQty = seed?.lines[0] ? Number(seed.lines[0].qty) : 0;

  const [header, setHeader] = useState(() => fixForm ? { ...fixForm.header } : ({
    storeId: seed?.storeId ?? intent?.storeId ?? '',
    adjustmentDate: initial ? initial.adjustmentDate.slice(0, 10) : todayIso(),
    reason: initial?.reason ?? '',
    direction: seedFirstQty < 0 ? '-1' : '1',
  }));
  const [lines, setLines] = useState<DrawLine[]>(() =>
    fixForm
      ? fixForm.lines.map(({ key: _old, ...line }) => emptyDrawLine(line))
      : seed && seed.lines.length > 0
      ? seed.lines.map((l) =>
          emptyDrawLine({
            skuId: l.skuId,
            skuLabel: `${l.skuCode} — ${l.skuName}`,
            qty: initial ? Math.abs(Number(l.qty)).toString() : '',
            batchId: l.batchId ?? '',
          }),
        )
      : [emptyDrawLine({ skuId: intent?.skuId ?? '', batchId: intent?.batchId ?? '' })],
  );
  const [problem, setProblem] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [reviewing, setReviewing] = useState(false);

  // Typing survives a reload, a crash or a closed window. Editing one document
  // must not offer another one's typing, so the draft is named after it.
  const draft = useDraft(
    editing ? `adjustment.edit.${initial.id}` : fixing ? `adjustment.fix.${fixing.id}` : 'adjustment.new',
    { header, lines },
    (saved) => {
      setHeader(saved.header);
      // Row keys come from a counter that restarts with the page, so restored
      // rows take fresh ones rather than colliding with a later form's.
      setLines(saved.lines.map(({ key: _old, ...rest }) => emptyDrawLine(rest)));
    },
  );

  function startAgain() {
    setHeader((h) => ({ ...h, reason: '' }));
    setLines([emptyDrawLine()]);
    setProblem(null);
  }

  const { options: storeOptions } = useFarmScopedStore(stores, header.storeId, (storeId) =>
    setHeader((h) => (h.storeId === storeId ? h : { ...h, storeId })),
  );

  const startedLines = lines.filter((l) => l.skuId || l.qty);

  function validate(): string | null {
    if (!header.storeId) return 'Choose the store.';
    if (!header.reason.trim()) return "Say why this adjustment is needed — it is the whole audit trail.";
    if (startedLines.length === 0) return 'Add at least one item.';
    if (startedLines.some((l) => !l.skuId)) return 'Every line with a quantity needs an item.';
    if (startedLines.some((l) => !(Number(l.qty) > 0))) return 'Every item needs a quantity above zero.';
    if (startedLines.some((l) => catalog.known.get(l.skuId)?.isBatchTracked && !l.batchId)) {
      return 'Choose which lot each lot-tracked item is for.';
    }
    return null;
  }

  const save = useMutation({
    mutationFn: async (): Promise<{ documentNo: string; waiting: boolean }> => {
      const payloadLines = startedLines.map((l) => ({
        skuId: l.skuId,
        qty: `${header.direction === '-1' ? '-' : ''}${l.qty}`,
        ...(l.batchId ? { batchId: l.batchId } : {}),
      }));
      const body = {
        storeId: header.storeId,
        adjustmentDate: header.adjustmentDate,
        reason: header.reason.trim(),
        lines: payloadLines,
      };
      const form = { header, lines: startedLines } satisfies QueuedAdjustmentForm;
      const describes = `Adjustment ${shortDate(header.adjustmentDate)}: ${startedLines.length} item(s), ${header.reason.trim()}`;

      if (fixing) {
        // Sent again under a new key.
        const newId = await retryWork(fixing.id, { body, form, describes });
        const saved = newId ? await awaitWork([newId]) : null;
        if (!saved || saved.outcome === 'refused' || saved.outcome === 'not-kept') {
          throw new Error(saved?.ops[0]?.lastError ?? 'The server still refused it. It is kept under Offline work, Needs attention.');
        }
        const made = saved.ops[0]?.result as { documentNo?: string } | undefined;
        return { documentNo: made?.documentNo ?? '', waiting: saved.outcome === 'waiting' };
      }

      // Through the outbox (.docs/023): answered now with a line, kept on this
      // PC without one. A change carries the version that was seen.
      const saved = await saveWork([
        initial
          ? {
              kind: 'adjustment.edit',
              method: 'PATCH',
              path: `${ADJUSTMENTS}/${initial.id}`,
              body: { version: initial.version, adjustmentDate: body.adjustmentDate, reason: body.reason, lines: payloadLines },
              describes: `Changing ${initial.documentNo}`,
              screen: 'inv-adjust',
            }
          : { kind: 'adjustment.create', method: 'POST', path: ADJUSTMENTS, body, describes, screen: 'inv-adjust', form },
      ]);
      const made = (initial ? { documentNo: initial.documentNo } : saved.ops[0]?.result) as { documentNo?: string } | undefined;
      if (saved.outcome === 'sent' || saved.outcome === 'waiting') {
        return { documentNo: made?.documentNo ?? '', waiting: saved.outcome === 'waiting' };
      }
      for (const op of saved.ops) if (op.state !== 'sent') await takeBack(op.id).catch(() => undefined);
      throw new Error(saved.ops.find((op) => op.state === 'refused')?.lastError ?? UNREACHABLE_MESSAGE);
    },
    onSuccess: (result) => {
      invalidate();
      draft.forget();
      if (result.waiting) {
        const what = editing ? `The change to ${initial.documentNo}` : fixing ? fixing.describes : `The adjustment of ${lines.filter((l) => l.skuId).length} item(s)`;
        if (editing || fixing) {
          onSaved(LATER);
          void announceSavedOffline({ what, closeLabel: 'OK' });
        } else {
          setMessage(LATER);
          void announceSavedOffline({ what, canEnterAnother: true, note: 'It gets its document number when it is sent.' }).then((choice) => {
            if (choice !== 'another') onSaved(LATER);
          });
        }
      } else if (editing || fixing) {
        onSaved(`${result.documentNo} saved. It still needs someone else's approval.`);
        return;
      } else {
        setMessage(`${result.documentNo} raised. Someone else must approve it before it can post.`);
      }
      if (editing || fixing) return;
      setLines([emptyDrawLine()]);
      setHeader((h) => ({ ...h, reason: '' }));
    },
  });

  function submit() {
    setMessage(null);
    const issue = validate();
    setProblem(issue);
    if (!issue) save.mutate();
  }

  function openReview() {
    setMessage(null);
    const issue = validate();
    setProblem(issue);
    if (!issue) setReviewing(true);
  }

  const storeName = storeOptions.find((s) => s.id === header.storeId);

  const panel = (
    <ComposePanel
      icon="adjust"
      splitId="compose-adjustment"
      title={editing ? `Edit ${initial.documentNo}` : fixing ? 'Put right a refused adjustment' : 'Raise a stock adjustment'}
      intro="For stock that is genuinely different from what the system says — damage, spillage, a miscount. Someone other than you has to approve it, and the reason is the audit trail."
      onCancel={() => {
        draft.forget();
        onCancel();
      }}
      cancelLabel={editing ? 'Cancel' : 'Back to list'}
      top={
        <div className="stack">
          <DraftNotice draft={draft} onStartAgain={startAgain} />
          <FormGroup title="Where and which way" template="2.4fr 1fr 1.4fr">
            <StorePicker
              label="Store"
              required
              disabled={editing}
              value={header.storeId}
              onChange={(storeId) => setHeader((h) => ({ ...h, storeId }))}
              stores={stores && storeOptions}
              hint={editing ? 'The store of a raised adjustment cannot change' : undefined}
            />
            <Field label="Date" type="date" required value={header.adjustmentDate} onChange={(adjustmentDate) => setHeader((h) => ({ ...h, adjustmentDate }))} />
            <Select
              label="Direction"
              required
              value={header.direction}
              onChange={(direction) => setHeader((h) => ({ ...h, direction }))}
              options={[
                { value: '-1', label: 'Write off (reduce stock)' },
                { value: '1', label: 'Write on (increase stock)' },
              ]}
              placeholder=""
              hint="Applies to every item below"
            />
            <TextArea
              label="Reason"
              required
              value={header.reason}
              onChange={(reason) => setHeader((h) => ({ ...h, reason }))}
              placeholder="e.g. three bags torn in storage"
              rows={2}
              hint="Whoever reviews this sees only what you write here."
              span="full"
            />
          </FormGroup>
        </div>
      }
      bottom={
        <>
          <FormGroup title="What is being adjusted" hint={header.storeId ? 'Search by item code or name.' : 'Choose a store first.'}>
            <DrawLines
              lines={lines}
              onChange={setLines}
              skus={catalog.known}
              search={catalog.search}
              storeId={header.storeId}
              checkAvailable={header.direction === '-1'}
            />
          </FormGroup>

        </>
      }
      footer={
        <>
          {problem && <ErrorNote message={problem} />}
          {save.isError && <ErrorNote message={errorMessage(save.error, 'Could not save')} />}
          {message && <OkNote message={message} />}

          <FormActions onCancel={onCancel} cancelLabel={editing ? 'Cancel' : 'Close'}>
            <button type="button" className="btn btn--primary" disabled={save.isPending} onClick={openReview}>
              Review & Save
            </button>
          </FormActions>
        </>
      }
    />
  );

  return (
    <>
      {panel}
      {reviewing && (
        <ReviewSummaryDialog
          icon="adjust"
          title={editing ? `Review ${initial.documentNo}` : 'Review before raising'}
          facts={[
            ['Store', storeName ? storeLabel(storeName) : '—'],
            ['Date', header.adjustmentDate],
            ['Direction', header.direction === '-1' ? 'Write off (reduce stock)' : 'Write on (increase stock)'],
            ['Reason', header.reason.trim() || '—'],
          ]}
          lines={startedLines.map((l) => {
            const sku = catalog.known.get(l.skuId);
            return {
              item: l.skuLabel || sku?.code || l.skuId,
              qty: sku ? `${quantity(l.qty, sku.uomPrecision)}${packedSuffix(l.qty, sku)}` : l.qty,
            };
          })}
          onClose={() => setReviewing(false)}
          primary={{
            label: editing ? 'Save changes' : 'Raise adjustment',
            pending: save.isPending,
            onClick: () => {
              setReviewing(false);
              submit();
            },
          }}
        />
      )}
    </>
  );
}
